import React, { FC, FocusEvent, useMemo } from 'react'
import Downshift from 'downshift'
import { RemoveScrollBar } from 'react-remove-scroll-bar'
import { system as s } from '@moonpig/launchpad-system'
import { styled, breakpointUp, breakpointDown } from '@moonpig/launchpad-utils'
import { ScreenReaderOnly, Box } from '@moonpig/launchpad-components'
import { spacingPx } from '@moonpig/launchpad-theme'
import { useGetBreakpoint } from '@moonpig/web-core-utils'
import { useStoreId } from '@moonpig/web-core-stores'
import { Input } from './Input'
import { SubmitButton } from './SubmitButton'
import { ClearButton } from './ClearButton'
import { BackButton } from './BackButton'
import { useLocaleText } from '../locale'
import {
  DepartmentTabItemType,
  InspirationType,
  RecentSearchType,
  ReminderItemType,
  SearchItemType,
  SubmitItemType,
  SuggestionItemType,
} from './types'
import { FocusableSearchBox } from './FocusableSearchBox'
import { AutocompleteSearchBox } from './AutocompleteSearchBox'
import { NavigationalSuggestionType } from './NavigationalSuggestions/NavigationalSuggestion'
import { SuggestionType } from './Suggestions/Suggestion'
import { Reminder } from './Reminders/Reminder'
import {
  DepartmentType,
  departmentMap,
  filterStoreAllowedDepartments,
  getDepartmentByName,
} from './departmentMap'
import { DepartmentTabs } from './DepartmentTabs/DepartmentTabs'

const StyledForm = styled.form.attrs<{ isActive: boolean }>(({ isActive }) => ({
  className: isActive ? 'is-active' : undefined,
}))<{ isActive: boolean } & React.HTMLAttributes<HTMLFormElement>>`
  height: ${spacingPx(12)};
  position: relative;

  &.is-active {
    z-index: 7;
    ${s({ bgcolor: 'colorBackground01' })}
  }
`

const GreyPanelOverlay = styled(Box)`
  ${breakpointUp('md')} {
    ${s({ bgcolor: 'colorBlack100' })}
    display: block;
    opacity: 0.4;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1;
  }
`

const WhitePanelOverlay = styled(Box)`
  ${s({ bgcolor: 'colorBackground01' })}
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100vw;
  height: 100dvh;
  opacity: 1;
  z-index: 2;

  ${breakpointUp('md')} {
    display: none;
  }
`

const StyledWrapper = styled.div.attrs<{ isActive: boolean }>(
  ({ isActive }) => ({
    className: isActive ? 'is-active' : undefined,
  }),
)<{ isActive: boolean }>`
  ${s({ bgcolor: 'colorBackground01' })}
  position: relative;
  top: 0;
  opacity: 1;
  z-index: 7;
  &.is-active {
    ${breakpointDown('md')} {
      position: fixed;
      right: 0;
      left: 0;
    }
  }
`

type OnSubmit = (submitItem: SubmitItemType) => void

type OnKeyUp = (value: string) => void
type OnFocus = (value: string) => void
type OnClear = () => void
type OnSuggestionsVisible = () => void
type OnRemindersVisible = () => void
type ClearButtonAs = ({ value }: { value: string }) => React.ReactNode
type OnRemoveRecentSearch = (value: RecentSearchType) => void
type OnClearRecentSearches = () => void

type SearchProps = {
  items: SuggestionType[]
  navigationalSuggestions?: NavigationalSuggestionType[]
  inspirations?: InspirationType[]
  recentSearches?: RecentSearchType[]
  reminders?: Reminder[]
  onSubmit: OnSubmit
  onTabChange: (tab: string) => void
  onKeyUp: OnKeyUp
  onFocus: OnFocus
  onSuggestionsVisible: OnSuggestionsVisible
  onRemoveRecentSearch: OnRemoveRecentSearch
  onClearRecentSearches: OnClearRecentSearches
  onRemindersVisible: OnRemindersVisible
  suggestionsLoading?: boolean
  onClear?: OnClear
  clearButtonAs?: ClearButtonAs
  searchTerm?: string
  maxSuggestions?: number
  maxNavigationalSuggestions?: number
  navigationalHeaderText?: string
  searchDepartment?: {
    name: string
  }
}

export const Search: FC<React.PropsWithChildren<SearchProps>> = ({
  items,
  onSubmit,
  onTabChange,
  onKeyUp,
  onFocus,
  onClear,
  clearButtonAs,
  searchTerm = '',
  onSuggestionsVisible,
  onRemoveRecentSearch,
  onClearRecentSearches,
  onRemindersVisible,
  maxSuggestions,
  navigationalSuggestions = [],
  maxNavigationalSuggestions,
  navigationalHeaderText,
  inspirations,
  recentSearches = [],
  reminders = [],
  suggestionsLoading,
  searchDepartment,
}) => {
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)
  const [activeDepartment, setDepartment] = React.useState<DepartmentType>(
    departmentMap.All,
  )

  const searchInput = React.useRef<HTMLInputElement>(null)
  const t = useLocaleText()
  const breakpoint = useGetBreakpoint()
  const storeId = useStoreId()

  const isMobile = useMemo(() => {
    return breakpoint === 'xs'
  }, [breakpoint])

  const handleInputFocus = (searchOpen: boolean) => {
    setIsSearchOpen(searchOpen)
    if (searchOpen) {
      onFocus(searchTerm)
    }
  }

  const setActiveDepartment = (selectedDepartment: DepartmentType) => {
    onTabChange(selectedDepartment.label.default)
    setDepartment(selectedDepartment)
  }

  const closeSearch = (reset?: () => void) => {
    /* istanbul ignore next */
    if (reset) {
      reset()
    }
    if (searchInput.current) {
      searchInput.current.blur()
    }
    handleInputFocus(false)
  }

  const handleClearClick = (reset: () => void) => {
    reset()
    if (onClear) {
      onClear()
    }
    if (searchInput.current) {
      searchInput.current.focus()
    }
  }

  const handleSubmit = (submitItem: SubmitItemType | DepartmentTabItemType) => {
    const selectedDepartment =
      activeDepartment.name === 'all'
        ? getDepartmentByName(searchDepartment?.name || 'all_cards')
        : activeDepartment
    const isSuggestionItem = submitItem.itemType === 'suggestion'
    const isReminderItem = submitItem.itemType === 'reminder'

    const itemWithDepartment = {
      ...submitItem,
      department: {
        name: isSuggestionItem
          ? submitItem.department?.name
          : selectedDepartment.name,
        title: isSuggestionItem
          ? submitItem.department?.title
          : selectedDepartment.title,
        activeDepartment: {
          name: activeDepartment.name,
          enum: activeDepartment.enum,
        },
        numberOfItems:
          activeDepartment.name === 'all'
            ? items.length
            : items.filter(
                suggestion =>
                  suggestion.department.enum === activeDepartment.enum,
              ).length,
      },
    }

    if (submitItem.itemType === 'department') {
      return setActiveDepartment({ ...submitItem.value })
    }

    if (
      submitItem.itemType === 'search-fallback' ||
      submitItem.itemType === 'search' ||
      !submitItem.itemType
    ) {
      onSubmit(itemWithDepartment as SearchItemType)
    } else if (isSuggestionItem) {
      onSubmit(itemWithDepartment as SuggestionItemType)
    } else if (isReminderItem) {
      onSubmit(itemWithDepartment as ReminderItemType)
    } else {
      onSubmit(submitItem)
    }
    if (searchInput.current) {
      searchInput.current.blur()
      handleInputFocus(false)
      closeSearch()
    }
  }

  const handleBlur = (event: FocusEvent) => {
    if (
      event.relatedTarget &&
      !event.currentTarget.contains(event.relatedTarget)
    ) {
      handleInputFocus(false)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    // istanbul ignore next
    if (!isSearchOpen && event.key !== 'Escape') {
      handleInputFocus(true)
    }
    /* istanbul ignore next */
    if (!isSearchOpen) return

    if (event.key === 'Escape') {
      if (searchInput.current) {
        searchInput.current.focus()
      }
      setIsSearchOpen(false)
    }
  }

  return (
    <StyledForm
      data-testid="lp-nav-search-form"
      isActive={isSearchOpen}
      onBlur={handleBlur}
      onFocus={() => {
        setIsSearchOpen(true)
      }}
      onKeyDown={handleKeyDown}
    >
      <Downshift
        isOpen={isSearchOpen}
        onChange={selection => {
          if (selection) {
            handleSubmit(selection)
          }
        }}
        itemToString={item =>
          item ? item.value : searchInput.current?.value || ''
        }
        initialInputValue={searchTerm}
        stateReducer={(state, changes) => {
          /* istanbul ignore next */
          switch (changes.type) {
            case Downshift.stateChangeTypes.blurInput:
              return state

            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem:
              return {
                inputValue:
                  typeof changes.inputValue === 'string'
                    ? changes.inputValue
                    : searchInput.current?.value || '',
                selectedItem: changes.selectedItem,
              }
            default:
              return changes
          }
        }}
      >
        {({
          getInputProps,
          getLabelProps,
          getRootProps,
          getItemProps,
          getMenuProps,
          selectItem,
          isOpen,
          inputValue,
          highlightedIndex,
          reset,
          clearSelection,
          setHighlightedIndex,
        }) => {
          const inputProps = getInputProps({
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                if (highlightedIndex === null || highlightedIndex === -1) {
                  e.nativeEvent.preventDefault()
                  return selectItem({ value: inputValue })
                }
                /* istanbul ignore next */
                reset()
              }
              /* istanbul ignore next */
              return undefined
            },
            onKeyUp: () => onKeyUp(inputValue || /* istanbul ignore next */ ''),
            onFocus: () => handleInputFocus(true),
          })

          const { value, ...filteredInputProps } = inputProps

          return (
            <StyledWrapper
              isActive={isOpen}
              {...getRootProps()}
              data-testid="search"
            >
              <ScreenReaderOnly as="label" {...getLabelProps()}>
                {t('search.search')}
              </ScreenReaderOnly>
              {isOpen ? (
                <>
                  <RemoveScrollBar />
                  <WhitePanelOverlay data-testid="lp-nav-search-overlay" />
                  <GreyPanelOverlay
                    data-testid="lp-nav-search-grey-overlay"
                    onClick={() => closeSearch()}
                  />
                  <BackButton
                    onClick={() => closeSearch()}
                    aria-label={t('search.close_search')}
                  />
                </>
              ) : null}
              <Input
                {...filteredInputProps}
                data-testid="search-input"
                value={value as string}
                isSearchOpen={isOpen}
                placeholder={t(
                  isMobile
                    ? 'search.placeholder_short'
                    : 'search.placeholder_long',
                )}
                ref={searchInput}
                type="search"
              />
              <ClearButton
                hidden={!inputValue}
                onClick={() =>
                  handleClearClick(() => {
                    reset()
                    clearSelection()
                    handleInputFocus(true)
                  })
                }
                as={
                  (clearButtonAs &&
                    clearButtonAs({ value: inputValue || '' })) as
                    | keyof React.JSX.IntrinsicElements
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    | React.ComponentType<React.PropsWithChildren<any>>
                }
              />
              <SubmitButton
                disabled={!inputValue}
                onClick={() => {
                  handleSubmit({
                    itemType: 'search',
                    value: inputValue || /* istanbul ignore next */ '',
                  })
                  reset()
                  clearSelection()
                }}
              />
              {isOpen ? (
                <DepartmentTabs
                  selectedDepartment={activeDepartment}
                  departments={filterStoreAllowedDepartments(storeId)}
                  highlightedIndex={highlightedIndex}
                  getItemProps={getItemProps}
                  storeId={storeId}
                  setHighlightedIndex={setHighlightedIndex}
                  setActiveDepartment={setActiveDepartment}
                />
              ) : null}
              <FocusableSearchBox
                data-testid="inspirations-list"
                isOpen={isOpen}
                inspirations={inspirations || /* istanbul ignore next */ []}
                reminders={reminders}
                recentSearches={recentSearches}
                inputValue={inputValue}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                onRemindersVisible={onRemindersVisible}
                onRemoveRecentSearch={onRemoveRecentSearch}
                onClearRecentSearches={onClearRecentSearches}
                onSelectItem={
                  /* istanbul ignore next */ (
                    selectedValue: string | SubmitItemType,
                  ) => {
                    const isNotSubmitItem = typeof selectedValue === 'string'

                    return selectItem(
                      isNotSubmitItem
                        ? { value: selectedValue }
                        : selectedValue,
                    )
                  }
                }
                activeDepartment={activeDepartment}
                setHightlightedIndex={setHighlightedIndex}
              />
              <AutocompleteSearchBox
                isOpen={isOpen}
                suggestions={items}
                inputValue={inputValue}
                getItemProps={getItemProps}
                getMenuProps={getMenuProps}
                highlightedIndex={highlightedIndex}
                value={inputValue || ''}
                onSuggestionsVisible={onSuggestionsVisible}
                maxSuggestions={maxSuggestions}
                navigationalSuggestions={navigationalSuggestions}
                maxNavigationalSuggestions={maxNavigationalSuggestions}
                navigationalHeaderText={navigationalHeaderText}
                suggestionsLoading={suggestionsLoading}
                selectedDepartment={activeDepartment}
                setActiveDepartment={setActiveDepartment}
                setHighlightedIndex={setHighlightedIndex}
                onSelectItem={selectItem}
              />
            </StyledWrapper>
          )
        }}
      </Downshift>
    </StyledForm>
  )
}
